//#region IMPORTS
import {
  InstoreOrderLineTableRow,
  InstoreOrderLineCustomField,
} from "../../models/instoreOrder";
import { useForm, useFieldArray } from "react-hook-form";
import { useEffect, useMemo } from "react";
import { CellContext } from "@tanstack/react-table";
import normalizeCustomFields from "../../helpers/normalizeCustomFields";
import classNames from "../../helpers/classNames";
//#endregion

//#region INTERFACE
interface Props {
  onSubmitHandler: (
    columnId: string,
    value: InstoreOrderLineCustomField[],
    row: InstoreOrderLineTableRow
  ) => void;
  cellContext?: CellContext<InstoreOrderLineTableRow, any>;
  disabled?: boolean;
  readOnly?: boolean;
  cancel: () => void;
}
//#endregion

export default function InstoreOrderDetailTablePersoFieldArrayForm({
  onSubmitHandler,
  cellContext,
  disabled = false,
  readOnly = false,
  cancel,
}: Props) {
  //#region SETUP
  // init items
  const initialItems = useMemo<InstoreOrderLineCustomField[]>(() => {
    return normalizeCustomFields(cellContext?.row.original.custom_fields);
  }, [cellContext]);

  // react hook form
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<{ items: InstoreOrderLineCustomField[] }>({
    mode: "all",
    defaultValues: { items: initialItems },
  });

  const { fields, replace } = useFieldArray({
    control,
    name: "items",
  });
  //#endregion

  //#region LOGIC
  useEffect(() => {
    replace(initialItems);
  }, [initialItems, replace]);

  const submitForm = (data: { items: InstoreOrderLineCustomField[] }) => {
    if (!cellContext) return;
    onSubmitHandler(
      cellContext.column.id,
      data.items,
      cellContext.row.original
    );
  };
  //#endregion

  //#region RENDER
  return (
    <main className="space-y-8">
      <header>
        <h2 className="text-base font-semibold leading-7 text-gray-900">
          Personalisation values
        </h2>
      </header>

      <form onSubmit={handleSubmit(submitForm)}>
        <ul>
          {fields.map((field, index) => (
            <li key={field.id} className="mt-4">
              <label className="block text-sm font-medium leading-6 mb-1">
                {field.fieldName}
              </label>

              <input
                className={classNames(
                  readOnly ? "bg-slate-50 border-0" : "border",
                  "block w-full rounded-md p-2"
                )}
                {...register(`items.${index}.fieldValue` as const, {
                  required: `${field.fieldName} is required`,
                })}
                defaultValue={field.fieldValue}
                disabled={disabled || readOnly}
              />

              {errors.items?.[index]?.fieldValue && (
                <p className="text-red-500 text-sm">
                  {errors.items[index]?.fieldValue?.message as string}
                </p>
              )}
            </li>
          ))}
        </ul>

        {!readOnly ? (
          <div className="flex items-center justify-between gap-x-6 mt-4">
            <button
              onClick={cancel}
              type="button"
              className="text-sm font-semibold text-pink-700 hover:text-pink-600"
            >
              {readOnly ? "Close" : "Cancel"}
            </button>

            <button
              type="submit"
              disabled={!isValid || disabled}
              className={classNames(
                isValid
                  ? "bg-emerald-600 hover:bg-emerald-500"
                  : "bg-gray-600 opacity-40",
                "rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm"
              )}
            >
              {isSubmitting ? "saving..." : "save items"}
            </button>
          </div>
        ) : null}
      </form>
    </main>
  );
  //#endregion
}
